import dayjs from 'dayjs';
import React from 'react';

import { Grid, Form, DateInput, Tooltip } from '@mairistem/composants';

import { Section } from '@/components/Common';
import { MOTIFS_DISSOLUTION } from '@/constants';

interface InformationsDissolutionProps {
  dateDissolution?: string;
  identifiantTypeDissolution?: number;
  dateSignification?: string;
  dateMariage?: string;
  dateDecesPartenaire1?: string;
  dateDecesPartenaire2?: string;
  dateDecisionUnilaterale?: string;
  dateDeclarationConjointe?: string;
  errors?: Record<string, string>;
  onChange: (
    _e: any,
    data: { name: string; value?: any; checked?: boolean },
  ) => void;
}

const InformationsDissolution = (props: InformationsDissolutionProps) => {
  const {
    dateDissolution,
    identifiantTypeDissolution,
    dateSignification,
    dateMariage,
    dateDecesPartenaire1,
    dateDecesPartenaire2,
    dateDecisionUnilaterale,
    dateDeclarationConjointe,
    errors,
    onChange,
  } = props;

  const handleMotifChange = React.useCallback(
    (value: string) => {
      onChange(null, {
        name: 'conventionDeclaration.identifiantTypeDissolution',
        value,
      });
    },
    [onChange],
  );

  const handleDissolutionFieldChange = React.useCallback(
    (field: string, value: string | null) => {
      onChange(null, {
        name: `conventionDeclaration.${field}`,
        value,
      });
    },
    [onChange],
  );

  const handleDateChange = React.useCallback(
    (field: string, rawValue: string | Date | null) => {
      if (rawValue === null || rawValue === undefined || rawValue === '') {
        handleDissolutionFieldChange(field, null);

        // Si on vide le champ source, on vide aussi la date de dissolution
        const isMariage =
          [1, 2, 3].includes(identifiantTypeDissolution as number) &&
          field === 'dateMariage';
        const isDeces =
          [4, 5, 6].includes(identifiantTypeDissolution as number) &&
          (field === 'partenaire1DateDeces' ||
            field === 'partenaire2DateDeces');
        if (isMariage || isDeces) {
          handleDissolutionFieldChange('dateDissolution', null);
        }

        return;
      }

      const parsedDate =
        rawValue instanceof Date ? rawValue : new Date(rawValue);
      const nextValue = Number.isNaN(parsedDate.getTime())
        ? null
        : dayjs(parsedDate).format('YYYY-MM-DD');

      handleDissolutionFieldChange(field, nextValue);

      if (
        [1, 2, 3].includes(identifiantTypeDissolution as number) &&
        field === 'dateMariage'
      ) {
        // Auto-complétion de la date de dissolution
        // Si c'est un mariage
        handleDissolutionFieldChange('dateDissolution', nextValue);
      }

      if (
        identifiantTypeDissolution === 5 &&
        field === 'partenaire1DateDeces'
      ) {
        // Décès d'un seul partenaire
        handleDissolutionFieldChange('dateDissolution', nextValue);
      }
      if (
        identifiantTypeDissolution === 6 &&
        field === 'partenaire2DateDeces'
      ) {
        handleDissolutionFieldChange('dateDissolution', nextValue);
      }

      if (
        identifiantTypeDissolution === 4 &&
        (field === 'partenaire1DateDeces' || field === 'partenaire2DateDeces')
      ) {
        // Décès des deux partenaires
        const p1Date =
          field === 'partenaire1DateDeces'
            ? nextValue
            : dateDecesPartenaire1 || null;
        const p2Date =
          field === 'partenaire2DateDeces'
            ? nextValue
            : dateDecesPartenaire2 || null;

        if (p1Date && p2Date) {
          // Si les deux dates existent, on prend la plus ancienne
          // biome-ignore lint/complexity/useMathMinMax: Math.min renverrait un timestamp et non la Date
          const datePlusAncienne = p1Date < p2Date ? p1Date : p2Date;
          handleDissolutionFieldChange('dateDissolution', datePlusAncienne);
        } else if (p1Date) {
          handleDissolutionFieldChange('dateDissolution', p1Date);
        } else if (p2Date) {
          handleDissolutionFieldChange('dateDissolution', p2Date);
        } else {
          handleDissolutionFieldChange('dateDissolution', null);
        }
      }
    },
    [
      handleDissolutionFieldChange,
      identifiantTypeDissolution,
      dateDecesPartenaire1,
      dateDecesPartenaire2,
    ],
  );

  const champsCausesDissolution = React.useMemo(() => {
    // Mariage
    if (
      identifiantTypeDissolution === 1 ||
      identifiantTypeDissolution === 2 ||
      identifiantTypeDissolution === 3
    ) {
      return (
        <Grid.Column width={6}>
          <Form.Input label="Date de mariage" required>
            <Tooltip
              trigger={
                <div>
                  <DateInput
                    data-testid="assistant-dissolution-date-mariage"
                    value={dateMariage ? new Date(dateMariage) : null}
                    onChange={(value) => handleDateChange('dateMariage', value)}
                    error={!!errors?.dateMariage}
                  />
                </div>
              }
              type={errors?.dateMariage ? 'error' : ''}
              message={errors?.dateMariage}
            />
          </Form.Input>
        </Grid.Column>
      );
    }

    // Décès des partenaires
    if (identifiantTypeDissolution === 4) {
      return (
        <>
          <Grid.Column width={3}>
            <Form.Input label="Décès du partenaire 1" required>
              <Tooltip
                trigger={
                  <div>
                    <DateInput
                      data-testid="assistant-dissolution-date-deces-partenaire-1"
                      value={
                        dateDecesPartenaire1
                          ? new Date(dateDecesPartenaire1)
                          : null
                      }
                      onChange={(value) =>
                        handleDateChange('partenaire1DateDeces', value)
                      }
                      error={!!errors?.partenaire1DateDeces}
                    />
                  </div>
                }
                type={errors?.partenaire1DateDeces ? 'error' : ''}
                message={errors?.partenaire1DateDeces}
              />
            </Form.Input>
          </Grid.Column>
          <Grid.Column width={3}>
            <Form.Input label="Décès du partenaire 2" required>
              <Tooltip
                trigger={
                  <div>
                    <DateInput
                      data-testid="assistant-dissolution-date-deces-partenaire-2"
                      value={
                        dateDecesPartenaire2
                          ? new Date(dateDecesPartenaire2)
                          : null
                      }
                      onChange={(value) =>
                        handleDateChange('partenaire2DateDeces', value)
                      }
                      error={!!errors?.partenaire2DateDeces}
                    />
                  </div>
                }
                type={errors?.partenaire2DateDeces ? 'error' : ''}
                message={errors?.partenaire2DateDeces}
              />
            </Form.Input>
          </Grid.Column>
        </>
      );
    }

    // Décès du partenaire 1
    if (identifiantTypeDissolution === 5) {
      return (
        <Grid.Column width={6}>
          <Form.Input label="Décès du partenaire 1" required>
            <Tooltip
              trigger={
                <div>
                  <DateInput
                    data-testid="assistant-dissolution-date-deces-partenaire-1"
                    value={
                      dateDecesPartenaire1
                        ? new Date(dateDecesPartenaire1)
                        : null
                    }
                    onChange={(value) =>
                      handleDateChange('partenaire1DateDeces', value)
                    }
                    error={!!errors?.partenaire1DateDeces}
                  />
                </div>
              }
              type={errors?.partenaire1DateDeces ? 'error' : ''}
              message={errors?.partenaire1DateDeces}
            />
          </Form.Input>
        </Grid.Column>
      );
    }

    // Décès du partenaire 2
    if (identifiantTypeDissolution === 6) {
      return (
        <Grid.Column width={6}>
          <Form.Input label="Décès du partenaire 2" required>
            <Tooltip
              trigger={
                <div>
                  <DateInput
                    data-testid="assistant-dissolution-date-deces-partenaire-2"
                    value={
                      dateDecesPartenaire2
                        ? new Date(dateDecesPartenaire2)
                        : null
                    }
                    onChange={(value) =>
                      handleDateChange('partenaire2DateDeces', value)
                    }
                    error={!!errors?.partenaire2DateDeces}
                  />
                </div>
              }
              type={errors?.partenaire2DateDeces ? 'error' : ''}
              message={errors?.partenaire2DateDeces}
            />
          </Form.Input>
        </Grid.Column>
      );
    }

    // Décision unilatérale
    if (
      identifiantTypeDissolution === 7 ||
      identifiantTypeDissolution === 8 ||
      identifiantTypeDissolution === 9 ||
      identifiantTypeDissolution === 10
    ) {
      return (
        <>
          <Grid.Column width={3}>
            <Form.Input label="Décision unilatérale" required>
              <Tooltip
                trigger={
                  <div>
                    <DateInput
                      data-testid="assistant-dissolution-date-decision-unilaterale"
                      value={
                        dateDecisionUnilaterale
                          ? new Date(dateDecisionUnilaterale)
                          : null
                      }
                      onChange={(value) =>
                        handleDateChange('dateDeclarationUnitDi', value)
                      }
                      error={!!errors?.dateDeclaration}
                    />
                  </div>
                }
                type={errors?.dateDeclaration ? 'error' : ''}
                message={errors?.dateDeclaration}
              />
            </Form.Input>
          </Grid.Column>
          <Grid.Column width={3}>
            <Form.Input label="Signification" required>
              <Tooltip
                trigger={
                  <div>
                    <DateInput
                      data-testid="assistant-dissolution-date-signification"
                      value={
                        dateSignification ? new Date(dateSignification) : null
                      }
                      onChange={(value) =>
                        handleDateChange('dateSignification', value)
                      }
                      error={!!errors?.dateDeclaration}
                    />
                  </div>
                }
                type={errors?.dateDeclaration ? 'error' : ''}
                message={errors?.dateDeclaration}
              />
            </Form.Input>
          </Grid.Column>
        </>
      );
    }

    // Déclaration conjointe
    if (
      identifiantTypeDissolution === 11 ||
      identifiantTypeDissolution === 12
    ) {
      return (
        <Grid.Column width={6}>
          <Form.Input label="Déclaration conjointe" required>
            <Tooltip
              trigger={
                <div>
                  <DateInput
                    data-testid="assistant-dissolution-date-declaration-conjointe"
                    value={
                      dateDeclarationConjointe
                        ? new Date(dateDeclarationConjointe)
                        : null
                    }
                    onChange={(value) =>
                      handleDateChange('dateDeclarationJointDi', value)
                    }
                    error={!!errors?.dateDeclarationJointDi}
                  />
                </div>
              }
              type={errors?.dateDeclarationJointDi ? 'error' : ''}
              message={errors?.dateDeclarationJointDi}
            />
          </Form.Input>
        </Grid.Column>
      );
    }

    return <Grid.Column width={6} />;
  }, [
    identifiantTypeDissolution,
    dateMariage,
    dateSignification,
    dateDecesPartenaire1,
    dateDecesPartenaire2,
    dateDecisionUnilaterale,
    dateDeclarationConjointe,
    errors,
    handleDateChange,
  ]);

  return (
    <Section
      data-testid="assistant-declaration-dissolution"
      title="DÉCLARATION DISSOLUTION"
    >
      <Form>
        <Grid padded>
          <Grid.Row>
            <Grid.Column width={6}>
              <Form.Dropdown
                required
                selection
                label="Motif"
                data-testid="assistant-dissolution-motif"
                options={MOTIFS_DISSOLUTION}
                value={
                  identifiantTypeDissolution ?? MOTIFS_DISSOLUTION[0].value
                }
                onChange={(_, data) => handleMotifChange(data.value as string)}
              />
            </Grid.Column>
            {champsCausesDissolution}
            {identifiantTypeDissolution !== MOTIFS_DISSOLUTION[0].value && (
              <Grid.Column width={2}>
                <Form.Input label="Date de dissolution" required>
                  <Tooltip
                    trigger={
                      <div>
                        <DateInput
                          data-testid="assistant-dissolution-date"
                          value={
                            dateDissolution ? new Date(dateDissolution) : null
                          }
                          onChange={(value) =>
                            handleDateChange('dateDissolution', value)
                          }
                          error={!!errors?.dateDissolution}
                        />
                      </div>
                    }
                    type={errors?.dateDissolution ? 'error' : ''}
                    message={errors?.dateDissolution}
                  />
                </Form.Input>
              </Grid.Column>
            )}
          </Grid.Row>
        </Grid>
      </Form>
    </Section>
  );
};

export default InformationsDissolution;
